import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useNotification } from '../../contexts/NotificationContext';
import { supabase } from '../../services/supabase';
import { useData } from '../../contexts/DataContext';
import { DEFAULT_FEATURE_FLAGS } from '../../utils/featureFlags';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

interface FeatureFlagManagementProps {
  companyId: string;
}

const FeatureFlagManagement: React.FC<FeatureFlagManagementProps> = ({ companyId }) => {
  const { t } = useTranslation();
  const { isSuperAdmin } = usePermissions();
  const { showNotification } = useNotification();
  const { companies, triggerCompanyFetch } = useData();
  const navigate = useNavigate();
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>(DEFAULT_FEATURE_FLAGS);
  const [loading, setLoading] = useState(true);
  const [updatingFlags, setUpdatingFlags] = useState<Record<string, boolean>>({});

  useEffect(() => {
    // Redirect if not a super admin
    if (!isSuperAdmin()) {
      showNotification(t('common.unauthorized'), 'error');
      navigate('/');
      return;
    }

    // Load feature flags for the specified company
    const loadFeatureFlags = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('companies')
          .select('feature_flags')
          .eq('id', companyId)
          .single();

        if (error) {
          throw error;
        }

        // Stored values apply over the defaults, so flags added after the
        // company row was last saved still show up in the list.
        setFeatureFlags({ ...DEFAULT_FEATURE_FLAGS, ...(data.feature_flags || {}) });
      } catch (error) {
        console.error('Error loading feature flags:', error);
        showNotification(t('common.error_loading_data'), 'error');
      } finally {
        setLoading(false);
      }
    };

    loadFeatureFlags();
  }, [companyId, isSuperAdmin]);

  const handleToggleFlag = async (flag: string) => {
    if (!isSuperAdmin()) {
      showNotification(t('common.unauthorized'), 'error');
      return;
    }

    try {
      // Set the specific flag as updating
      setUpdatingFlags((prev) => ({ ...prev, [flag]: true }));

      const updatedFlags = {
        ...featureFlags,
        [flag]: !featureFlags[flag],
      };

      setFeatureFlags(updatedFlags);

      const { error } = await supabase
        .from('companies')
        .update({ feature_flags: updatedFlags })
        .eq('id', companyId);

      if (error) {
        throw error;
      }

      showNotification(t('common.feature_flag_updated'), 'success');
      await triggerCompanyFetch();
    } catch (error) {
      console.error('Error updating feature flag:', error);
      showNotification(t('common.error_updating_feature_flag'), 'error');

      // Revert the local state change in case of error
      setFeatureFlags((prevState) => ({
        ...prevState,
        [flag]: !prevState[flag],
      }));
    } finally {
      // Clear updating state for this flag
      setUpdatingFlags((prev) => ({ ...prev, [flag]: false }));
    }
  };

  const company = companies.find((c) => c.id === companyId);

  // Helper function to get feature flag display name
  const getFeatureFlagDisplayName = (flagKey: string): string => {
    const translationKey = `features.${flagKey}`;
    const translated = t(translationKey);
    // If translation doesn't exist, format the key nicely
    return translated === translationKey
      ? flagKey
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      : translated;
  };

  // Helper function to get feature flag description
  const getFeatureFlagDescription = (flagKey: string): string => {
    const translationKey = `features.${flagKey}_details`;
    const translated = t(translationKey);
    // If translation doesn't exist, provide a default description
    return translated === translationKey
      ? `Enable or disable ${flagKey.replace('_', ' ')} for the company`
      : translated;
  };

  if (!isSuperAdmin()) {
    return (
      <div className="p-4 bg-yellow-50 text-yellow-700 rounded-md">
        {t('common.unauthorized_feature')}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center p-4">
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  // Get all feature flag keys from the current state
  const featureFlagKeys = Object.keys(featureFlags);

  return (
    <div className="flex flex-col h-full overflow-hidden p-4 bg-white rounded-sm shadow">
      <h3 className="text-xl font-semibold flex-shrink-0 mb-4">
        {t('common.feature_flag_management')} - {company?.name || t('common.unknown_company')}
      </h3>

      <div className="flex-1 min-h-0 overflow-y-auto space-y-3">
        {featureFlagKeys.map((flagKey) => (
          <div key={flagKey} className="flex items-center justify-between p-3 border rounded-md">
            <div>
              <span className="font-medium">{getFeatureFlagDisplayName(flagKey)}</span>
              <p className="text-sm text-gray-500">{getFeatureFlagDescription(flagKey)}</p>
            </div>
            {updatingFlags[flagKey] ? (
              <div className="w-11 h-6 flex justify-center items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={featureFlags[flagKey] || false}
                  onChange={() => handleToggleFlag(flagKey)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

FeatureFlagManagement.propTypes = {
  companyId: PropTypes.string.isRequired,
};

export default FeatureFlagManagement;
